// @flow
/* eslint-disable import/max-dependencies */
import {
  combineReducers,
  applyMiddleware,
  createStore,
  compose,
} from "redux";
import type { Dispatch, Store, CombinedReducer } from "redux";
import createSagaMiddleware from "redux-saga";
import type { SagaMiddleware } from "redux-saga";
import Immutable, { Map } from "immutable";
import form, {
  initFormState,
  type State as FormState,
} from "@fas/ui-framework/lib/redux/reducers/form";
import { tableReducer as tables, initTableState, type TableState } from "@fas/ui-framework/lib/redux/reducers/table";
import type { Actions as TableActions } from "@fas/ui-framework/lib/redux/actions/table";
import loading, {
  initLoadingState,
  type State as LoadingState,
} from "@fas/ui-framework/lib/redux/reducers/loading";
import notifications, {
  type State as NotificationsState,
  initNotificationsState,
} from "@fas/ui-framework/lib/redux/reducers/notifications";
import {
  reducer as errors,
  type State as ErrorsState,
  initState as initErrorsState,
} from "@fas/ui-framework/lib/redux/reducers/errors";
import type { Actions as LoadingActions } from "@fas/ui-framework/lib/redux/actions/loading";
import type { Action as NotificationsActions } from "@fas/ui-framework/lib/redux/actions/notifications";
import mainSaga from "./saga";
import dictionaries, {
  type State as DictionariesState,
  initState as initDictionariesState,
} from "../../reducers/dictionaries";
import conditions, {
  type State as QueryBuilderState,
  makeState as initQueryBuilderState,
} from "../../reducers/segmentConditions";
import { FORM_KEY_SEGMENT, MESSAGE_GROUPS_LIST_TABLE } from "../../helpers/constants";
import type { SegmentFormData } from "../../containers/CreateSegment";
import { rulesList } from "../../helpers/QueryBuilder";

export const defaultFormData: SegmentFormData = {
  id: "",
  name: "",
  isActive: true,
  isLimit: false,
  minimum: 0,
  maximum: 0,
  webPushMessageGroups: [],
  criteriaRaw: "",
};

export type State = $ReadOnly<{|
  loading: LoadingState<string>,
  notifications: NotificationsState,
  errors: ErrorsState,
  form: FormState,
  dictionaries: DictionariesState,
  tables: TableState,
  conditions: QueryBuilderState,
|}>

export function mapSegmentsToState(): State {
  return {
    form: initFormState({
      [FORM_KEY_SEGMENT]: defaultFormData,
    }),
    loading: initLoadingState({
      [FORM_KEY_SEGMENT]: false,
    }),
    notifications: initNotificationsState(),
    errors: initErrorsState(),
    dictionaries: initDictionariesState(),
    tables: initTableState({
      [MESSAGE_GROUPS_LIST_TABLE]: { pageSize: 20 },
    }),
    conditions: initQueryBuilderState({
      rulesList: Map(rulesList),
    }),
  };
}

export type Actions = LoadingActions<string>
  | NotificationsActions
  | TableActions;

const reducers: CombinedReducer<State, Actions> = combineReducers({
  loading,
  notifications,
  errors,
  form,
  dictionaries,
  tables,
  conditions,
});

const sagaMiddleware: SagaMiddleware<{}> = createSagaMiddleware();

export default (): Store<State, Actions> => {
  // eslint-disable-next-line no-underscore-dangle, flowtype/require-variable-type
  const composeEnhancers = typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
    serialize: {
      immutable: Immutable,
    },
  }) : compose;

  const store: Store<State, Actions> = createStore<State, Actions, Dispatch<Actions>>(
    reducers,
    mapSegmentsToState(),
    composeEnhancers(applyMiddleware(
      sagaMiddleware
    ))
  );

  sagaMiddleware.run(mainSaga);

  return store;
};
